import { useState } from 'react';

import { Upload, AlertCircle } from 'lucide-react';

import { FormTextarea } from '@/components/form';
import { Button } from '@/components/ui/button';

import type { ParameterDefinition } from './ParameterEditor';

interface JSONImportDialogProps {
  onImport: (parameters: ParameterDefinition[], bodyTemplate?: unknown) => void;
}

export const JSONImportDialog = ({ onImport }: JSONImportDialogProps) => {
  const [jsonInput, setJsonInput] = useState('');
  const [error, setError] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const handleImport = () => {
    try {
      setError('');
      const parsed = JSON.parse(jsonInput);

      // Validate structure
      if (!parsed.parameters?.properties) {
        throw new Error('JSON must have a "parameters.properties" object');
      }

      // Convert to ParameterDefinition[]
      const parameters: ParameterDefinition[] = Object.entries(
        parsed.parameters.properties as Record<string, Record<string, unknown>>,
      ).map(([name, prop]) => {
        const param: ParameterDefinition = {
          name,
          type: (prop.type as ParameterDefinition['type']) || 'string',
          description: (prop.description as string) || '',
          required: (parsed.parameters.required as string[])?.includes(name) || false,
          enum: prop.enum as string[] | undefined,
          in: (prop.in as 'path' | 'query' | 'body' | 'filter' | 'display') || 'body',
          filter_config: prop.filter_config as ParameterDefinition['filter_config'],
          path_variable: prop.path_variable as string | undefined,
          isFixed: prop.fixed_value !== undefined,
          fixedValue: prop.fixed_value,
        };

        // Handle arrays
        if (prop.type === 'array' && prop.items) {
          const items = prop.items as Record<string, unknown>;
          param.items = {
            type:
              (items.type as 'string' | 'number' | 'integer' | 'boolean' | 'object' | 'array') ||
              'object',
            properties: items.properties
              ? Object.entries(items.properties as Record<string, Record<string, unknown>>).map(
                  ([nestedName, nestedProp]) => ({
                    name: nestedName,
                    type: (nestedProp.type as ParameterDefinition['type']) || 'string',
                    description: (nestedProp.description as string) || '',
                    required: (items.required as string[])?.includes(nestedName) || false,
                    enum: nestedProp.enum as string[] | undefined,
                    in: 'body' as const,
                  }),
                )
              : [],
            required: items.required as string[] | undefined,
          };
        }

        // Handle nested objects
        if (prop.type === 'object' && prop.properties) {
          param.properties = Object.entries(
            prop.properties as Record<string, Record<string, unknown>>,
          ).map(([nestedName, nestedProp]) => ({
            name: nestedName,
            type: (nestedProp.type as ParameterDefinition['type']) || 'string',
            description: (nestedProp.description as string) || '',
            required: (prop.required as string[])?.includes(nestedName) || false,
            enum: nestedProp.enum as string[] | undefined,
            in: 'body' as const,
          }));
        }

        return param;
      });

      onImport(parameters, parsed.parameters?.body_template);
      setIsOpen(false);
      setJsonInput('');
      setError('');
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'Invalid JSON format');
    }
  };

  if (!isOpen) {
    return (
      <Button type="button" variant="outline" size="sm" onClick={() => setIsOpen(true)}>
        <Upload size={16} className="mr-1" />
        Import from JSON
      </Button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background mx-4 max-h-[80vh] w-full max-w-2xl overflow-auto rounded-lg shadow-xl">
        <div className="space-y-4 p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Import Parameters from JSON</h3>
            <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
              ✕
            </Button>
          </div>

          <p className="text-muted-foreground text-sm">
            Paste your tool&apos;s JSON schema below. It should be in the format with a
            &quot;parameters.properties&quot; object.
          </p>

          <FormTextarea
            rows={15}
            placeholder={`{
  "name": "capture_availability",
  "description": "Tool description...",
  "parameters": {
    "type": "object",
    "required": ["availability_slots"],
    "properties": {
      "availability_slots": {
        "type": "array",
        "description": "List of slots",
        "items": {
          "type": "object",
          "required": ["date", "shift_type"],
          "properties": {
            "date": { "type": "string", "description": "Date in YYYY-MM-DD" },
            "shift_type": { "type": "string", "enum": ["morning", "evening"] }
          }
        }
      }
    }
  }
}`}
            value={jsonInput}
            onChange={(e) => setJsonInput(e.target.value)}
          />

          {error && (
            <div className="bg-destructive/10 text-destructive flex items-start gap-2 rounded-md p-3">
              <AlertCircle size={16} className="mt-0.5" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleImport}>
              Import Parameters
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
